import { useElementSize } from "@mantine/hooks";
import { useI18n } from "@wojo/localization";
import { DepartureHit } from "@wojo/services";
import { Button, LoadingSkeleton, Text } from "@wojo/ui";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { groupBy } from "lodash";
import React, { useMemo, useRef } from "react";
import {
    TripBuilderDepartureDetailType,
    useTourPageContext,
} from "../../../_client/context";
import { useDeparturesStepContext } from "../context/DeparturesStepContext";
import { useTripBuilder } from "../context/TripBuilderContext";
import { DepartureListClassModal } from "./DepartureListClassModal";
import { DepartureListHeadingCell } from "./DepartureListHeadingCell";
import { DepartureListPriceModal } from "./DepartureListPriceModal";
import { DepartureListRow } from "./DepartureListRow";
import { DepartureListYear } from "./DepartureListYear";
import { QuoteExtensionsModal } from "../trip-builder-quote-step/QuoteExtensionsModal";
import styles from "./TripBuilderDeparturesStep.module.scss";
import { DepartureYearToggle } from "./DepartureYearToggle";

dayjs.extend(utc);

export const DepartureList: React.FC = () => {
    const { tourContent, pageConfig } = useTourPageContext();
    const { departureDetailType } = useTripBuilder();
    const { departures, isLoading, setFilterStates } =
        useDeparturesStepContext();
    const { productPage } = useI18n("productPage");
    const { ref, height } = useElementSize();
    const departureListRef = useRef<HTMLDivElement>(null);
    const departuresByYear = useMemo(
        () =>
            groupBy(departures.hits, (departure) =>
                dayjs.unix(departure.departureDate).utc().year(),
            ),
        [departures.hits],
    );
    const departureYears = useMemo(
        () => Object.keys(departuresByYear),
        [departuresByYear],
    );

    const getFlexHeadingLabel = () => {
        switch (departureDetailType) {
            case TripBuilderDepartureDetailType.ClassLevel:
                return (
                    <>
                        {productPage.tripBuilder.departures.columnClassLevel.l()}
                        <DepartureListClassModal departures={departures.hits} />
                    </>
                );
            case TripBuilderDepartureDetailType.Extension:
            default:
                return (
                    <>
                        {productPage.tripBuilder.departures.columnExtension.l()}
                        <QuoteExtensionsModal
                            isIconTrigger
                            content={tourContent.extension}
                        />
                    </>
                );
        }
    };

    return (
        <>
            {!!pageConfig.displayTripBuilderYearToggle && (
                <DepartureYearToggle
                    years={departureYears}
                    listRef={departureListRef}
                    headerHeight={height}
                />
            )}
            <div
                ref={departureListRef}
                style={{
                    maxHeight: "600px",
                    overflowY: "auto",
                    borderRadius: "inherit",
                    marginTop: "var(--g-spacing-sm)",
                }}
            >
                <table
                    className={styles.table}
                    aria-label={productPage.tripBuilder.departures.heading.l()}
                >
                    <thead className={styles.header} ref={ref}>
                        <tr>
                            <DepartureListHeadingCell
                                style={{ textAlign: "left" }}
                            >
                                {productPage.tripBuilder.departures.column1.l()}
                            </DepartureListHeadingCell>
                            <DepartureListHeadingCell>
                                {getFlexHeadingLabel()}
                            </DepartureListHeadingCell>
                            <DepartureListHeadingCell>
                                {productPage.tripBuilder.departures.column3.l()}
                                <DepartureListPriceModal />
                            </DepartureListHeadingCell>
                        </tr>
                    </thead>
                    {departureYears.map((year) => {
                        return isLoading ? (
                            <tbody key="loading">
                                <tr>
                                    <td colSpan={3}>
                                        <LoadingFallback />
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody
                                data-year={year}
                                key={year}
                                style={{ position: "relative" }}
                                data-testid="departure-list"
                            >
                                <DepartureListYear
                                    tabIndex={-1}
                                    style={{ top: height - 2 }}
                                >
                                    {year}
                                </DepartureListYear>
                                {departuresByYear[year].map(
                                    (departure: DepartureHit) => {
                                        return (
                                            <DepartureListRow
                                                key={departure.id}
                                                departure={departure}
                                            />
                                        );
                                    },
                                )}
                            </tbody>
                        );
                    })}

                    <tbody key="clear-filters">
                        <tr>
                            <td colSpan={3}>
                                {departures.nbHits === 0 && (
                                    <Text
                                        style={{
                                            padding: "var(--g-spacing-md)",
                                            display: "grid",
                                            justifyContent: "center",
                                            rowGap: "var(--g-spacing-md)",
                                        }}
                                    >
                                        {productPage.tripBuilder.departures.noResults.l()}
                                        <Button
                                            variant="quiet"
                                            onClick={async () => {
                                                await setFilterStates({
                                                    departureEndDate: null,
                                                    departureStartDate: null,
                                                    filters: null,
                                                    tripTypes: null,
                                                });
                                            }}
                                        >
                                            Clear all filters
                                        </Button>
                                    </Text>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </>
    );
};
const LoadingFallback = () => {
    const { productPage } = useI18n("productPage");
    return (
        <div
            style={{
                marginBottom: "var(--g-spacing-md)",
                paddingLeft: "var(--g-spacing-md)",
                paddingRight: "var(--g-spacing-md)",
            }}
        >
            <LoadingSkeleton
                style={{ marginBottom: "var(--g-spacing-md)" }}
                accessibleText={productPage.tripBuilder.departures.loadingAccessibleText.l()}
                height="40px"
            />
            {[...Array(6)].map((_, index) => (
                <div className={styles["fallback-departure"]} key={index}>
                    <LoadingSkeleton
                        accessibleText=""
                        className={styles["fallback-departure-icon"]}
                        height="24px"
                        width="24px"
                    />
                    <LoadingSkeleton
                        className={styles["fallback-departure-line1"]}
                        accessibleText=""
                        height="24px"
                        width="100%"
                    />
                    <LoadingSkeleton
                        className={styles["fallback-departure-line2"]}
                        accessibleText=""
                        height="24px"
                        width="50%"
                    />
                </div>
            ))}
        </div>
    );
};
